import React from 'react'
import { Table, Tag, Button, message, Spin, Row, Space, Col, Avatar } from 'antd'
import { EditOutlined, DeleteOutlined, DownOutlined } from '@ant-design/icons'
import { useDeleteBlog } from 'api/Blogs/Blogs'
import { Popconfirm } from 'antd';
import { useQueryClient } from '@tanstack/react-query'
import { formatDateTime } from 'utils/dateUtils';
import Loader from 'helper/Loader';
import { Visibility } from 'types/blogs/blogs';
import { getBlogStatusColors } from 'helper/blogStatusColors';
import usePagination from 'helper/usePagination';


interface BlogPostsTableProps {
	blogs: any;
	blogsLoading: boolean;
	onEdit?: (blogId: string) => void;
	refetch?: () => void;
	params: any;
	setParams: React.Dispatch<React.SetStateAction<any>>;
}

export const BlogPostsTable = (props: BlogPostsTableProps) => {
	const queryClient = useQueryClient()
	const { mutate: deleteBlogPost, isLoading } = useDeleteBlog()
	const { onEdit } = props
	const [showMoreAuthors, setShowMoreAuthors] = React.useState<string[]>([])

	const [paginationInfo, onTableChange] = usePagination({
		totalCount: props.blogs?.count || 0,
		currentPage: props.params.page,
		setParams: props.setParams,
	})

	const handleDelete = (id: string) => {
		deleteBlogPost(id, {
			onSuccess: () => {
				queryClient.invalidateQueries(['all-blogs'])
				props.refetch?.();
			},
			onError: (error: any) => {
				console.error('Delete error:', error)
			}
		})
	}

	const StatusFormat = (status: string) => {
		if (status === 'public') {
			return 'PUBLIC'
		} else if (status === 'draft') {
			return 'DRAFT'
		}
	}


	const columns = [
		{
			title: 'Title',
			dataIndex: 'title',
			key: 'title',
			width: 200,
			sorter: (a: any, b: any) => a.title?.localeCompare(b.title),
			render: (text: string) => (
				<span style={{ color: '#000000', cursor: 'pointer', fontWeight: '400', fontSize: '14px' }}>{text}</span>
			),
		},
		{
			title: 'Authors',
			dataIndex: 'authors',
			key: 'authors',
			width: 230,
			sorter: (a: any, b: any) => (a.authors?.length || 0) - (b.authors?.length || 0),
			render: (authors: any[], record: any) => {
				const isExpanded = showMoreAuthors.includes(record._id)

				return (
					<div className="kids-list">
						<Row gutter={[8, 8]}>
							{authors?.map((author: any, index: number) => {
								if (isExpanded || index < 2) {
									return (
										<Col key={author._id || index}>
											<Space size={8} align="start">
												<Avatar src={author.photo?.post}>
													{author.firstName?.[0]}
												</Avatar>
												<div>
													<h3 className="text-14 w-400">
														{author.firstName} {author.lastName}
													</h3>
													<p className="text-12 w-400 grey-txt">{author.role || ''}</p>
												</div>
											</Space>
										</Col>
									)
								}
								return null
							})}

							{authors?.length > 2 && (
								<Col span={24}>
									{!isExpanded ? (
										<h5
											className="cursor_pointer"
											style={{ color: '#2BA7CA', margin: 0 }}
											onClick={() => setShowMoreAuthors([...showMoreAuthors, record._id])}
										>
											Show More <DownOutlined />
										</h5>
									) : (
										<h5
											className="cursor_pointer"
											style={{ color: '#2BA7CA', margin: 0 }}
											onClick={() =>
												setShowMoreAuthors(showMoreAuthors.filter(id => id !== record._id))
											}
										>
											Show Less <DownOutlined style={{ transform: 'rotate(180deg)' }} />
										</h5>
									)}
								</Col>
							)}
						</Row>
					</div>
				)
			},
		},
		{
			title: 'Created',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 150,
			sorter: (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
			render: (createdAt: string) => {
				const dateTime = formatDateTime(createdAt);
				const parts = dateTime.split(',');
				return (
					<span style={{ color: '#000000', cursor: 'pointer', fontWeight: '400', fontSize: '14px' }}>
						{parts[0]}
						<span style={{ color: '#2BA7CA', fontWeight: '700' }}>
							{parts[1] ? `${parts[1]}` : ''}
						</span>
					</span>
				);
			},
		},
		{
			title: 'Published',
			dataIndex: 'dateTime',
			key: 'dateTime',
			width: 130,
			sorter: (a: any, b: any) => {
				const dateA = a.scheduledAt || a.createdAt;
				const dateB = b.scheduledAt || b.createdAt;
				return new Date(dateA).getTime() - new Date(dateB).getTime();
			},
			render: (_: any, record: any) => {
				const date = record.scheduledAt || record.createdAt;
				if (!date) {
					return (
						<span style={{ color: '#999999', cursor: 'pointer', fontWeight: '400', fontSize: '14px' }}>
							Not set
						</span>
					);
				}
				const dateTime = formatDateTime(date);
				const parts = dateTime.split(',');
				return (
					<span style={{ color: '#000000', cursor: 'pointer', fontWeight: '400', fontSize: '14px' }}>
						{parts[0]}
						<span style={{ color: '#2BA7CA', fontWeight: '700' }}>
							{parts[1] ? `${parts[1]}` : ''}
						</span>
					</span>
				);
			},
		},
		{
			title: 'Status',
			dataIndex: 'visibility',
			key: 'visibility',
			width: 120,
			sorter: (a: any, b: any) => a.visibility.localeCompare(b.visibility),
			render: (visibility: Visibility) => {
				const { backgroundColor, color } = getBlogStatusColors(visibility)

				return (
					<Tag
						style={{
							border: "none",
							backgroundColor,
							color,
							borderRadius: '15px',
							padding: '4px 10px',
							fontWeight: 'bold',
						}}
					>
						{visibility?.toUpperCase() || 'UNKNOWN'}
					</Tag>
				)
			},
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 100,
			render: (_, record) => (
				<div style={{ display: 'flex', gap: '20px' }}>
					<Button
						type="text"
						icon={<EditOutlined />}
						size="small"
						style={{ border: 'none' }}
						onClick={() => onEdit && onEdit(record?._id)}
					/>
					{record?.visibility !== Visibility.ARCHIVED ? (
						<Popconfirm
							title="Are you sure to delete this blog post?"
							okText="Yes"
							cancelText="No"
							onConfirm={() => handleDelete(record?._id)}
						>
							<Button
								type="text"
								loading={isLoading}
								icon={<DeleteOutlined />}
								size="small"
								danger
							/>
						</Popconfirm>
					) : null}
				</div>
			),
		},
	]



	const rowSelection = {
		onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
			console.log(
				`selectedRowKeys: ${selectedRowKeys}`,
				'selectedRows: ',
				selectedRows,
			)
		},
		getCheckboxProps: (record: any) => ({
			disabled: false,
			name: record.title,
		}),
	}

	return (
		<div style={{ padding: '20px', position: 'relative' }}>
			{/* Full-page loading overlay */}
			{isLoading && (
				<div style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: 'rgba(255, 255, 255, 0.8)',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					zIndex: 9999,
				}}>
					<Loader />
				</div>
			)}

			<Table
				rowKey="_id"
				columns={columns}
				dataSource={props.blogs?.records || []}
				loading={props.blogsLoading}
				pagination={{
					...paginationInfo,
					pageSize: props.params.limit || 10,
				}}
				onChange={onTableChange}
				rowSelection={rowSelection}
				size="middle"
				style={{
					border: '1px solid #f0f0f0',
					borderRadius: '8px',
				}}
				className="blog-posts-table"
				components={{
					header: {
						cell: (props: any) => (
							<th
								{...props}
								style={{
									backgroundColor: '#EBEDF1',
									fontWeight: '600',
									color: '#000000'
								}}
							/>
						),
					},
				}}
			/>
		</div>
	)
}

export default BlogPostsTable
